import React from 'react';
import { PageHeader, Section, StatRow } from '../components/PageShell';
import { Button } from '../components/Button';
import { Check } from 'lucide-react';

const values = [
  { title: 'AI First',              desc: 'AI is at the core of every product we design, not an afterthought.' },
  { title: 'Enterprise-Grade Craft', desc: 'Every deliverable is audit-, security- and scale-ready by default.' },
  { title: 'Long-Term Partnership',  desc: 'We stay after launch. Roadmap, iterate, evolve &mdash; year after year.' },
  { title: 'Radical Transparency',   desc: 'Live environments, weekly demos, honest status. No theatre.' },
  { title: 'Global, Remote-First',   desc: 'A senior team distributed across continents, engineered around your timezone.' },
  { title: 'Outcomes over Output',   desc: 'We optimise for the business metric, not the ticket count.' },
];

const leadership = [
  { name: 'Yash Patel',   role: 'Founder &amp; CEO',           bio: 'Enterprise software strategist. 12+ years shipping mission-critical platforms across facility management, retail and healthcare.' },
  { name: 'Priya Menon',  role: 'CTO',                          bio: 'AI &amp; cloud architect. Led AI platform engineering at scale for logistics and manufacturing groups.' },
  { name: 'Rahul Sharma', role: 'VP Engineering',              bio: 'Ex-consulting principal. Runs the delivery machine &mdash; from discovery to SLA-backed maintenance.' },
  { name: 'Sara Ahmed',   role: 'Head of Product &amp; Design',bio: 'Product-led engineering leader. Turns messy enterprise problems into shippable, usable systems.' },
];

const process = [
  { step: '01', title: 'Discovery',          desc: 'Understand business, users and risk before writing code.' },
  { step: '02', title: 'Business Analysis',  desc: 'Translate goals into measurable outcomes and a shaped scope.' },
  { step: '03', title: 'UI/UX',              desc: 'Prototype the experience &mdash; validated with real users.' },
  { step: '04', title: 'Architecture',       desc: 'Cloud-native, secure and scalable system design.' },
  { step: '05', title: 'Development',        desc: 'Weekly increments, live environments, no surprises.' },
  { step: '06', title: 'Quality Assurance',  desc: 'Automated + manual QA, security and performance testing.' },
  { step: '07', title: 'Deployment',         desc: 'Zero-downtime rollouts with observability wired in.' },
  { step: '08', title: 'Maintenance',        desc: 'SLA-backed operations and continuous improvement.' },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pb-24">
      <PageHeader
        eyebrow="About YP Nova"
        title={<>An <span className="font-serif">international</span> AI &amp; enterprise software engineering company.</>}
        subtitle="YP Nova helps businesses automate, scale and innovate through artificial intelligence, enterprise software and cloud engineering. We design, build and operate systems that leadership teams bet the business on."
      >
        <div className="flex flex-wrap gap-3">
          <Button to="/contact" variant="primary">Work with us</Button>
          <Button to="/case-studies" variant="secondary">See our work</Button>
        </div>
      </PageHeader>

      <Section>
        <StatRow
          items={[
            { value: '2019',  label: 'Founded' },
            { value: '30+',   label: 'Engineers, designers, PMs' },
            { value: '11',    label: 'Industries served' },
            { value: '60+',   label: 'Enterprise engagements' },
          ]}
        />
      </Section>

      {/* Mission / Vision */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-[32px] bg-white border border-[#7C3AED]/12 p-8 md:p-10 shadow-[0_4px_16px_rgba(0,0,0,0.04)]">
            <p className="font-mono text-xs uppercase tracking-widest text-[#273C46] mb-4">Mission</p>
            <h2 className="text-[26px] md:text-[34px] leading-[1.1] text-[#0D212C] tracking-tight">
              Helping businesses <span className="font-serif">automate, scale and innovate</span> through AI, enterprise software and cloud engineering.
            </h2>
          </div>
          <div className="rounded-[32px] bg-gradient-to-br from-[#051A24] via-[#1E1B4B] to-[#4C1D95] text-white p-8 md:p-10 shadow-[0_20px_60px_rgba(76,29,149,0.25)]">
            <p className="font-mono text-xs uppercase tracking-widest text-[#E0EBF0] mb-4">Vision</p>
            <h2 className="text-[26px] md:text-[34px] leading-[1.1] tracking-tight">
              To be the <span className="font-serif italic">engineering partner</span> that ambitious enterprises trust with their most important software.
            </h2>
          </div>
        </div>
      </Section>

      {/* Core Values */}
      <Section>
        <div className="mb-10 md:mb-14">
          <p className="font-mono text-xs uppercase tracking-widest text-[#273C46] mb-3">Core Values</p>
          <h2 className="text-[32px] md:text-[44px] leading-[1.05] text-[#0D212C] tracking-tight">
            What we <span className="font-serif">believe</span>.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {values.map((v) => (
            <div key={v.title} className="p-6 rounded-3xl bg-white border border-[#7C3AED]/12 shadow-[0_4px_16px_rgba(0,0,0,0.04)]">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full grad-icon text-white flex items-center justify-center shadow-[0_6px_20px_rgba(109,40,217,0.28)] flex-shrink-0">
                  <Check className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-[#0D212C] mb-1">{v.title}</h3>
                  <p
                    className="text-sm text-[#273C46] leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: v.desc }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Leadership */}
      <Section>
        <div className="mb-10 md:mb-14">
          <p className="font-mono text-xs uppercase tracking-widest text-[#273C46] mb-3">Leadership</p>
          <h2 className="text-[32px] md:text-[44px] leading-[1.05] text-[#0D212C] tracking-tight">
            Senior <span className="font-serif">operators,</span> not middlemen.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {leadership.map((l) => (
            <div key={l.name} className="rounded-[28px] bg-white border border-[#7C3AED]/12 p-6 shadow-[0_4px_16px_rgba(0,0,0,0.04)]">
              <div className="w-14 h-14 rounded-2xl grad-icon text-white flex items-center justify-center shadow-[0_6px_20px_rgba(109,40,217,0.28)] text-lg font-serif mb-5">
                {l.name.split(' ').map((n) => n[0]).join('')}
              </div>
              <h3 className="font-serif text-xl text-[#0D212C] mb-1">{l.name}</h3>
              <p
                className="text-xs font-mono uppercase tracking-widest text-[#273C46] mb-3"
                dangerouslySetInnerHTML={{ __html: l.role }}
              />
              <p
                className="text-sm text-[#273C46] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: l.bio }}
              />
            </div>
          ))}
        </div>
      </Section>

      {/* Why Choose */}
      <Section>
        <div className="rounded-[40px] bg-white border border-[#7C3AED]/12 p-8 md:p-14 shadow-[0_4px_16px_rgba(0,0,0,0.04)]">
          <p className="font-mono text-xs uppercase tracking-widest text-[#273C46] mb-4">Why Choose YP Nova</p>
          <h2 className="text-[28px] md:text-[40px] leading-[1.1] text-[#0D212C] tracking-tight mb-10">
            The <span className="font-serif">eight-phase</span> engagement, without the ceremony.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p) => (
              <div key={p.step} className="p-6 rounded-2xl bg-[#F6FCFF]">
                <div className="font-mono text-xs text-[#273C46] mb-3">{p.step}</div>
                <h3 className="font-serif text-2xl text-[#0D212C] mb-2">{p.title}</h3>
                <p
                  className="text-sm text-[#273C46] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: p.desc }}
                />
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Philosophy */}
      <Section>
        <div className="rounded-[40px] bg-gradient-to-br from-[#051A24] via-[#1E1B4B] to-[#4C1D95] text-white p-10 md:p-16 shadow-[0_20px_60px_rgba(76,29,149,0.25)] relative overflow-hidden">
          <p className="font-mono text-xs uppercase tracking-widest text-[#E0EBF0] mb-4">Our Philosophy</p>
          <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] mb-6">
            &ldquo;Your Imagination. Our Creation.&rdquo;
          </h2>
          <p className="text-[#E0EBF0] max-w-2xl leading-relaxed">
            The best enterprise software feels inevitable once it exists. We work
            shoulder-to-shoulder with founders, CTOs and operators to translate
            ambitious business ideas into scalable, secure and beautiful software
            &mdash; and stay long enough to see it compound.
          </p>
          <div className="mt-8">
            <Button to="/contact" variant="secondary" className="!bg-white !text-[#051A24]">
              Book a Discovery Call
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
}
